import { CircleCheck, ImageUp, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { csrfToken } from '@/lib/csrf';
import { cn } from '@/lib/utils';
import type { ApplyPhotoCandidate, ApplyPhotoStep } from '@/types';
import type { ProfileFormData } from './types';

interface Props {
  ownerName: string;
  step: ApplyPhotoStep;
  /** The (normalised) LinkedIn URL from the personal step, whose photo is a candidate. */
  linkedinUrl: string;
  data: ProfileFormData;
  onChange: (patch: Partial<ProfileFormData>) => void;
  /** The applicant confirmed skipping: record it and move on. */
  onSkip: () => void;
  /** The signed ids of the verified photos on offer (empty while loading). */
  onOptionsChange: (ids: string[]) => void;
}

const SOURCE_LABELS: Record<ApplyPhotoCandidate['source'], string> = {
  github: 'GitHub',
  linkedin: 'LinkedIn',
  upload: 'Subida por ti',
};

// The profile-photo step (Apply::ProfilePhotosController): the GitHub and LinkedIn photos
// that pass the server's checks (a real person's face, in colour), plus uploads checked
// the same way. Choosing one stores nothing yet — the wizard's final save attaches it.
export function PhotoStep({
  ownerName,
  step,
  linkedinUrl,
  data,
  onChange,
  onSkip,
  onOptionsChange,
}: Props) {
  const [candidates, setCandidates] = useState<ApplyPhotoCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState<ApplyPhotoCandidate[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch(
      `${step.candidatesUrl}?linkedin=${encodeURIComponent(linkedinUrl)}`,
      {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      },
    )
      .then((response) => (response.ok ? response.json() : { candidates: [] }))
      .then((body: { candidates: ApplyPhotoCandidate[] }) => {
        setCandidates(body.candidates ?? []);
        setLoading(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('[photo-candidates]', error);
        setCandidates([]);
        setLoading(false);
      });
    return () => controller.abort();
  }, [step.candidatesUrl, linkedinUrl]);

  const select = (candidate: ApplyPhotoCandidate) => {
    if (!candidate.ok || !candidate.signedId) return;
    onChange({
      photoSignedId: candidate.signedId,
      photoUrl: candidate.url,
      photoSkipped: false,
    });
  };

  const upload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const body = new FormData();
      body.append('photo', file);
      const response = await fetch(step.uploadUrl, {
        method: 'POST',
        body,
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const result = (await response.json()) as Partial<ApplyPhotoCandidate>;
      if (result.ok && result.signedId) {
        const candidate = result as ApplyPhotoCandidate;
        setUploads((previous) => [...previous, candidate]);
        select(candidate);
      } else {
        setUploadError(result.error ?? 'No pudimos verificar esta foto.');
      }
    } catch (error) {
      console.error('[photo-upload]', error);
      setUploadError('No pudimos subir la foto. Intenta de nuevo.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  // The photo already chosen (a previous save or this session) leads the list when it is
  // not one of the fresh candidates.
  const offered = [...candidates.filter((c) => c.ok), ...uploads];
  const current = step.current;
  const options =
    current && !offered.some((c) => c.signedId === current.signedId)
      ? [current, ...offered]
      : offered;
  const refused = candidates.filter((c) => !c.ok);

  // Tell the wizard which photos can be continued with — none while loading — and drop a
  // selection that is not among them (a stale draft, a photo that no longer verifies).
  const optionIds = loading
    ? []
    : options.flatMap((c) => (c.signedId ? [c.signedId] : []));
  const optionKey = optionIds.join('|');
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the ids themselves
  useEffect(() => {
    onOptionsChange(optionIds);
    if (
      !loading &&
      data.photoSignedId !== null &&
      !optionIds.includes(data.photoSignedId)
    ) {
      onChange({ photoSignedId: null, photoUrl: null });
    }
  }, [optionKey, loading]);

  return (
    <div className="space-y-6">
      <h2 className="font-bold text-3xl">{ownerName}.photo</h2>
      <p className="text-secondary-foreground text-sm">
        Estamos preparando una sorpresa para cada hacker 🤫. Para eso
        necesitamos una foto tuya, a color y donde se te vea bien la cara.
      </p>

      {loading ? (
        <p className="flex items-center gap-2 text-muted-foreground text-sm">
          <LoaderCircle className="size-4 animate-spin" />
          buscando tus fotos de GitHub y LinkedIn…
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {options.map((candidate) => {
            const selected = data.photoSignedId === candidate.signedId;
            return (
              <button
                key={candidate.signedId}
                type="button"
                onClick={() => select(candidate)}
                aria-pressed={selected}
                className={cn(
                  'relative overflow-hidden border-2 bg-card text-left transition',
                  selected
                    ? 'border-primary ring-2 ring-primary ring-offset-2 ring-offset-background'
                    : data.photoSignedId
                      ? 'border-transparent opacity-50 hover:opacity-100'
                      : 'border-transparent hover:border-primary/60',
                )}
              >
                <img
                  src={candidate.url ?? ''}
                  alt={`Foto de ${SOURCE_LABELS[candidate.source]}`}
                  className="aspect-square w-full object-cover"
                />
                <span className="flex items-center justify-between gap-1 px-2 py-1.5 text-xs">
                  {SOURCE_LABELS[candidate.source]}
                  {selected && <CircleCheck className="size-4 text-primary" />}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="flex aspect-square flex-col items-center justify-center gap-2 border-2 border-border border-dashed bg-card p-3 text-center text-xs hover:border-primary/60 disabled:cursor-wait"
          >
            {uploading ? (
              <>
                <LoaderCircle className="size-6 animate-spin" />
                verificando tu foto…
              </>
            ) : (
              <>
                <ImageUp className="size-6" />
                subir otra foto
              </>
            )}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload(file);
            }}
          />
        </div>
      )}

      {uploadError && (
        <p className="text-destructive text-xs" aria-live="polite">
          {uploadError}
        </p>
      )}

      {!loading && refused.length > 0 && (
        <ul className="space-y-1 text-muted-foreground text-xs">
          {refused.map((candidate) => (
            <li key={candidate.source}>
              Tu foto de {SOURCE_LABELS[candidate.source]} no nos sirve:{' '}
              {candidate.error}
            </li>
          ))}
        </ul>
      )}

      {!loading && options.length === 0 && !uploadError && (
        <p className="text-sm">
          No encontramos una foto tuya que nos sirva en GitHub ni en LinkedIn.
          Sube una foto donde se te vea la cara.
        </p>
      )}

      <button
        type="button"
        onClick={() => setSkipOpen(true)}
        className="text-muted-foreground text-xs underline underline-offset-2"
      >
        prefiero saltar este paso
      </button>

      <AlertDialog open={skipOpen} onOpenChange={setSkipOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Saltar la foto?</AlertDialogTitle>
            <AlertDialogDescription>
              Sin una foto tuya te vas a perder la sorpresa que estamos
              preparando para cada hacker. High risk, low reward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Elegir una foto</AlertDialogCancel>
            <AlertDialogAction
              onClick={onSkip}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Saltar y perderme la sorpresa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
